/* Native shell integration (Capacitor, Android + iOS).
 *
 * The same web bundle runs on towardsgm.web.app and inside the native
 * apps; every export here is a safe no-op on the plain web so nothing in
 * the app needs to care where it runs. Keep ALL Capacitor imports behind
 * this module — the rest of src/ must stay platform-agnostic.
 */
import { Capacitor } from "@capacitor/core";
import { App as CapApp } from "@capacitor/app";
import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";
import { LocalNotifications } from "@capacitor/local-notifications";

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // "web" | "android" | "ios"

/* Hardware back button (Android): last registered handler wins. A handler
 * returns true when it consumed the press (closed a modal, left a course
 * view); when nothing consumes it the app is minimized, never killed. */
const backHandlers = [];

export function onHardwareBack(handler) {
  backHandlers.push(handler);
  return () => {
    const index = backHandlers.indexOf(handler);
    if (index !== -1) backHandlers.splice(index, 1);
  };
}

/* Haptic feedback for the drill loop. Fires independently of the sound
 * toggle — a phone on silent should still feel right/wrong answers. */
export function haptic(kind) {
  if (!isNative) return;
  const run =
    kind === "correct"
      ? Haptics.notification({ type: NotificationType.Success })
      : kind === "wrong"
        ? Haptics.notification({ type: NotificationType.Error })
        : Haptics.impact({ style: ImpactStyle.Light }); // "move" and taps
  run.catch(() => {});
}

/* ---- local notifications (Phase 0 item 4: one helper, several consumers) ----
 *
 * LOCAL, not push. The app has no server, so there is nothing to send a push
 * from — but a local notification is queued on the device by the OS and fires
 * whether or not the app is running, which is everything a practice reminder
 * needs. Web push (FCM + a sender) is a separate, larger job; `notify.js`
 * carries the honest web fallback in the meantime.
 *
 * Every export is a no-op on the plain web, per this module's contract, and
 * every promise is caught: a phone that has revoked the permission mid-session
 * should quietly schedule nothing, not throw into a render. */

export async function notificationPermission() {
  if (!isNative) return "unsupported";
  try {
    return (await LocalNotifications.checkPermissions()).display;
  } catch {
    return "unsupported";
  }
}

export async function requestNotificationPermission() {
  if (!isNative) return "unsupported";
  try {
    return (await LocalNotifications.requestPermissions()).display;
  } catch {
    return "denied";
  }
}

/* Replaces the queue rather than adding to it: callers pass every id they own
 * plus the subset that still applies, so re-running after "I trained today"
 * removes today's reminder instead of leaving a stale copy behind. Doing it in
 * one call keeps that atomic from the caller's point of view. */
export async function setScheduledNotifications(ownedIds, notifications) {
  if (!isNative) return false;
  try {
    if (ownedIds?.length) await LocalNotifications.cancel({ notifications: ownedIds.map((id) => ({ id })) });
    if (notifications?.length) {
      await LocalNotifications.schedule({
        notifications: notifications.map((n) => ({
          id: n.id,
          title: n.title,
          body: n.body,
          schedule: { at: new Date(n.at), allowWhileIdle: true },
          extra: { kind: n.kind },
        })),
      });
    }
    return true;
  } catch {
    return false;
  }
}

export function initNative() {
  if (!isNative) return;

  // App-like viewport only inside the shells: the website keeps pinch zoom.
  const viewport = document.querySelector("meta[name=viewport]");
  if (viewport) {
    viewport.content =
      "width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover";
  }
  document.documentElement.classList.add("native-app");

  // Light chrome (dark status-bar icons) to match the bright theme. Android
  // is handled by the SystemBars config in capacitor.config.json; iOS needs
  // the call.
  if (platform === "ios") {
    StatusBar.setStyle({ style: Style.Light }).catch(() => {});
  }

  CapApp.addListener("backButton", () => {
    for (let index = backHandlers.length - 1; index >= 0; index--) {
      if (backHandlers[index]()) return;
    }
    CapApp.minimizeApp().catch(() => {});
  });

  /* A tapped reminder should land on the thing it was nagging about. The
   * shell is already open by the time this fires, so it is an in-app
   * navigation rather than a launch route — announced as an event so
   * native.js stays free of app structure. */
  LocalNotifications.addListener("localNotificationActionPerformed", (event) => {
    window.dispatchEvent(new CustomEvent("towardsgm-notification-opened", { detail: { kind: event?.notification?.extra?.kind || null } }));
  }).catch(() => {});

  // The splash stays up (launchAutoHide false) until React has painted the
  // first frame; double-rAF is the cheapest "after first paint" signal.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      SplashScreen.hide().catch(() => {});
    });
  });
}

/* Will this platform actually carry these files? `navigator.canShare` is the
 * only honest test and it has to be asked BEFORE sharing: Firefox implements
 * share() with no file support at all, and a payload the platform refuses
 * throws rather than degrading to the text. Exported so a caller can tell
 * whether the picture it built is going to travel — this file stays the one
 * place that knows what the platform can do. */
export function canShareFiles(files) {
  return Boolean(
    files?.length &&
      typeof navigator !== "undefined" &&
      typeof navigator.canShare === "function" &&
      navigator.canShare({ files })
  );
}

/* W3b · share a short text — and, where the platform takes them, files — the
 * best way this platform can: the Web Share API where it exists (iOS
 * WKWebView, mobile browsers), otherwise the clipboard.
 * No @capacitor/share plugin on purpose — it is a native dependency, which
 * means new store builds for one button. Returns "shared", "copied",
 * "cancelled" or "unavailable" so the caller can say what happened.
 *
 * The text goes out either way: the clipboard fallback cannot carry a picture,
 * and a Daily result is legible as text by design (that is the whole point of
 * the emoji row), so an image is an enrichment and never the payload. */
export async function shareText(text, files = null) {
  try {
    if (typeof navigator !== "undefined" && navigator.share) {
      await navigator.share(canShareFiles(files) ? { files, text } : { text });
      return "shared";
    }
  } catch (err) {
    if (err?.name === "AbortError") return "cancelled";
  }
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return "copied";
    }
  } catch {
    /* permission denied or insecure context — fall through */
  }
  return "unavailable";
}
